import { useState } from 'react'
import { gql, useQuery } from 'urql'
import { ExternalLink } from 'lucide-react'
import { Card, CardContent } from './ui/card'
import { Button } from './ui/button'

const BUNDLE_DEAL_QUERY = gql`
  query BundleDeal($merchant: String!) {
    bundleDeal(merchant: $merchant) {
      description
      sourceUrl
    }
  }
`

type Insight = {
  title: string
  body: string
  annualSaving: number
  category: string
  merchant?: string | null
}

export function InsightCard({ insight }: { insight: Insight }) {
  const [checked, setChecked] = useState(false)
  const [{ data, fetching }] = useQuery({
    query: BUNDLE_DEAL_QUERY,
    variables: { merchant: insight.merchant ?? '' },
    pause: !checked,
    requestPolicy: 'network-only',
  })

  const deal = data?.bundleDeal

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
      <CardContent className="p-6">
        <div className="mb-4">
          <span className="text-4xl font-bold text-amber-600">${Math.round(insight.annualSaving).toLocaleString()}</span>
          <span className="text-lg font-medium text-amber-400">/yr</span>
        </div>
        <p className="text-base font-semibold text-gray-900 mb-1.5">{insight.title}</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{insight.body}</p>

        {insight.category === 'subscription' && insight.merchant && !checked && (
          <Button
            variant="outline"
            size="sm"
            className="mt-4 border-amber-300 text-amber-800 hover:bg-amber-100"
            onClick={() => setChecked(true)}
          >
            Check for a better deal
          </Button>
        )}

        {checked && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            {fetching ? (
              <p className="text-xs text-muted-foreground">Checking…</p>
            ) : deal ? (
              <div>
                <p className="text-sm text-gray-700 leading-relaxed mb-1">{deal.description}</p>
                <p className="text-xs text-muted-foreground mb-2">Double check current pricing before switching.</p>
                <a
                  href={deal.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:underline"
                >
                  View source <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nothing on file for this one yet.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
